// src/components/Booklet/InteractiveElementModal.js
import React, { useState, useEffect } from 'react';
import { X, Link, Video, Volume2, MousePointer, Upload } from 'lucide-react';

const InteractiveElementModal = ({ isOpen, type, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    type: type,
    x: 20,
    y: 20,
    width: 30,
    height: 20,
    data: {}
  });

  useEffect(() => {
    if (isOpen && type) {
      setFormData({
        type: type,
        x: 20,
        y: 20,
        width: 30,
        height: 20,
        data: getDefaultData(type)
      });
    }
  }, [isOpen, type]);

  const getDefaultData = (elementType) => {
    switch (elementType) {
      case 'link':
        return { url: 'https://', text: 'Click here', openInNewTab: true };
      case 'video':
        return { videoUrl: '', autoplay: false, controls: true };
      case 'audio':
        return { audioUrl: '', autoplay: false, loop: false };
      case 'popup':
        return { title: 'Popup Title', content: 'Popup content goes here...' };
      default:
        return {};
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (type === 'link' && !formData.data.url?.trim()) {
      alert('Please enter a valid URL');
      return;
    }
    if (type === 'video' && !formData.data.videoUrl?.trim()) {
      alert('Please enter a video URL');
      return;
    }
    if (type === 'audio' && !formData.data.audioUrl?.trim()) {
      alert('Please enter an audio URL');
      return;
    }
    if (type === 'popup' && !formData.data.title?.trim()) {
      alert('Please enter a popup title');
      return;
    }

    onSave(formData);
    onClose();
  };

  const updateData = (key, value) => {
    setFormData(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [key]: value
      }
    }));
  };

  const getIcon = () => {
    switch (type) {
      case 'link': return <Link className="w-6 h-6 text-blue-600" />;
      case 'video': return <Video className="w-6 h-6 text-red-600" />;
      case 'audio': return <Volume2 className="w-6 h-6 text-green-600" />;
      case 'popup': return <MousePointer className="w-6 h-6 text-purple-600" />;
      default: return null;
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'link': return 'Add Link';
      case 'video': return 'Add Video';
      case 'audio': return 'Add Audio';
      case 'popup': return 'Add Popup';
      default: return 'Add Element';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getIcon()}
            <h2 className="text-xl font-semibold text-gray-900">{getTitle()}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Position and Size */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Position & Size</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  X Position (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="95"
                  value={formData.x}
                  onChange={(e) => setFormData(prev => ({ ...prev, x: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Y Position (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="95"
                  value={formData.y}
                  onChange={(e) => setFormData(prev => ({ ...prev, y: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Width (%)
                </label>
                <input
                  type="number"
                  min="5"
                  max="50"
                  value={formData.width}
                  onChange={(e) => setFormData(prev => ({ ...prev, width: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Height (%)
                </label>
                <input
                  type="number"
                  min="5"
                  max="50"
                  value={formData.height}
                  onChange={(e) => setFormData(prev => ({ ...prev, height: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Type-specific fields */}
          {type === 'link' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Link Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL *
                  </label>
                  <input
                    type="url"
                    value={formData.data.url}
                    onChange={(e) => updateData('url', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="https://example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link Text
                  </label>
                  <input
                    type="text"
                    value={formData.data.text}
                    onChange={(e) => updateData('text', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Click here"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="openInNewTab"
                    checked={formData.data.openInNewTab}
                    onChange={(e) => updateData('openInNewTab', e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="openInNewTab" className="ml-2 text-sm text-gray-700">
                    Open in new tab
                  </label>
                </div>
              </div>
            </div>
          )}

          {type === 'video' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Video Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video URL *
                  </label>
                  <input
                    type="url"
                    value={formData.data.videoUrl}
                    onChange={(e) => updateData('videoUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supports YouTube, Vimeo, and direct video URLs
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="autoplay"
                    checked={formData.data.autoplay}
                    onChange={(e) => updateData('autoplay', e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="autoplay" className="ml-2 text-sm text-gray-700">
                    Autoplay video
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="controls"
                    checked={formData.data.controls}
                    onChange={(e) => updateData('controls', e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="controls" className="ml-2 text-sm text-gray-700">
                    Show video controls
                  </label>
                </div>
              </div>
            </div>
          )}

          {type === 'audio' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Audio Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Audio URL *
                  </label>
                  <input
                    type="url"
                    value={formData.data.audioUrl}
                    onChange={(e) => updateData('audioUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="https://example.com/audio.mp3"
                    required
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="audioAutoplay"
                    checked={formData.data.autoplay}
                    onChange={(e) => updateData('autoplay', e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="audioAutoplay" className="ml-2 text-sm text-gray-700">
                    Autoplay audio
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="loop"
                    checked={formData.data.loop}
                    onChange={(e) => updateData('loop', e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="loop" className="ml-2 text-sm text-gray-700">
                    Loop audio
                  </label>
                </div>
              </div>
            </div>
          )}

          {type === 'popup' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Popup Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.data.title}
                    onChange={(e) => updateData('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Popup title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content
                  </label>
                  <textarea
                    value={formData.data.content}
                    onChange={(e) => updateData('content', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={4}
                    placeholder="Enter popup content..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Add Element
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InteractiveElementModal;